import { Currency } from "@pancakeswap/sdk";
import { ChainId } from "@pancakeswap/chains";
import { useMemo } from "react";
import { styled } from "styled-components";
import { useHttpLocations } from "@pancakeswap/hooks";
import { TokenLogo, BinanceIcon } from "@pancakeswap/uikit";

import { getCurrencyLogoUrls } from "./utils";

const StyledLogo = styled(TokenLogo)<{ size: string }>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  border-radius: 50%;
`;

// ponytail: META_ASSETS chain isn't on TrustWallet / PancakeSwap logo CDNs, so map the
// few known symbols to bundled local icons. Add a row here when a new token needs art.
const META_ASSETS_LOCAL_LOGOS: Record<string, string> = {
  MA: "/images/MA.png",
  WMA: "/images/MA.png",
  USDT: "/images/USDT.png",
  ETH: "/images/ETH.png",
  WETH: "/images/ETH.png",
  BTC: "/images/BTC.png",
  WBTC: "/images/BTC.png",
};

const isMetaAssetsChain = (chainId?: number) =>
  chainId === ChainId.META_ASSETS || chainId === ChainId.META_ASSETS_TESTNET;

const getMetaAssetsLocalLogo = (currency?: Currency): string | undefined =>
  isMetaAssetsChain(currency?.chainId) ? META_ASSETS_LOCAL_LOGOS[(currency?.symbol ?? "").toUpperCase()] : undefined;

export function CurrencyLogo({
  currency,
  size = "24px",
  style,
}: {
  currency?: Currency & {
    logoURI?: string | undefined;
  };
  size?: string;
  style?: React.CSSProperties;
}) {
  const uriLocations = useHttpLocations(currency?.logoURI);
  const localLogo = getMetaAssetsLocalLogo(currency);

  const srcs: string[] = useMemo(() => {
    if (currency?.isNative) return [];

    if (currency?.isToken) {
      const logoUrls = getCurrencyLogoUrls(currency);

      const allSrcs = localLogo ? [localLogo, ...logoUrls] : logoUrls;

      if (currency?.logoURI) {
        return [...uriLocations, ...allSrcs];
      }
      return [...allSrcs];
    }
    return [];
  }, [currency, uriLocations, localLogo]);

  if (currency?.isNative) {
    if (currency.chainId === ChainId.BSC) {
      return <BinanceIcon width={size} style={style} />;
    }
    if (isMetaAssetsChain(currency.chainId)) {
      return <StyledLogo size={size} srcs={["/images/MA.png"]} width={size} style={style} />;
    }
    return (
      <StyledLogo
        size={size}
        srcs={[`https://assets.pancakeswap.finance/web/native/${currency.chainId}.png`]}
        width={size}
        style={style}
      />
    );
  }

  return <StyledLogo size={size} srcs={srcs} alt={`${currency?.symbol ?? "token"} logo`} style={style} />;
}
